import { FormEvent, useState } from "react";
import { Button } from "../../elements/Button";
import { CancelButton } from "../../elements/CancelButton";
import { Modal } from "../../elements/Modal";
import { LookupField } from "../fields/LookupField";
import { PicklistField } from "../fields/PicklistField";
import { TextField } from "../fields/TextField";
import { MergeAttribute } from "./richTextInserts";
import { AttributeDialogProps } from "./RichTextDialogs.types";
import { useEntityAttributes } from "./useEntityAttributes";

/** Chooses an attribute to fill in for each recipient, with a default and a format. */
export function RichTextAttributeDialog({ initial, entities, onApply, onClose }: AttributeDialogProps) {
  const [value, setValue] = useState<MergeAttribute>(initial);
  const [errors, setErrors] = useState<{ attribute?: string }>({});
  const entity = entities.find((candidate) => candidate.key === value.entity) ?? entities[0];
  const { options: attributes } = useEntityAttributes(entity?.entityName ?? null);

  const set = <Key extends keyof MergeAttribute>(key: Key) => (next: MergeAttribute[Key]) =>
    setValue((current) => ({ ...current, [key]: next }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!value.attribute) {
      setErrors({ attribute: "Required" });
      return;
    }

    onApply(value);
  };

  return (
    <Modal title="Add Attribute" onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <div className="modal-body">
          {entities.length > 1 && (
            <PicklistField
              label="Entity"
              name="rt-attribute-entity"
              value={value.entity}
              options={entities.map((candidate) => ({ Key: candidate.key, Value: candidate.label }))}
              hidePlaceholder
              // The attribute belongs to an entity, so a new one starts the choice again.
              onChange={(next) => setValue((current) => ({ ...current, entity: next ?? "1", attribute: "", attributeName: "" }))}
            />
          )}
          <LookupField
            label="Attribute"
            name="rt-attribute-attribute"
            value={value.attribute}
            options={attributes}
            searchable
            required
            error={errors.attribute}
            onChange={(next) =>
              setValue((current) => ({
                ...current,
                attribute: next,
                attributeName: attributes.find((option) => option.id === next)?.displayName ?? "",
              }))
            }
          />
          <TextField
            label="Default Value"
            name="rt-attribute-default"
            value={value.defaultValue}
            helpText="Default value to use when the attribute is not set"
            onChange={set("defaultValue")}
          />
          <TextField
            label="Format String"
            name="rt-attribute-format"
            value={value.formatString}
            helpText="Format the value as a string. For example, represent a datetime value in the format dd/MM/yyyy."
            onChange={set("formatString")}
          />
        </div>
        <div className="modal-footer">
          <CancelButton onClick={onClose} />
          <Button variant="primary" type="submit" text="Confirm" />
        </div>
      </form>
    </Modal>
  );
}
